import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from "react"
import { useAuth } from "@/lib/auth-context"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Plus, ArrowUpCircle, ArrowDownCircle, Banknote } from "lucide-react"
import { formatBRDate, todayISODate } from "@/lib/br-dates"
import { API_BASE } from '@/lib/api-base'

interface Movimentacao {
  id: string
  conta_receber_id: string | null
  tipo: string
  categoria: string | null
  valor: number
  data_movimentacao: string
  descricao: string | null
  criado_em: string
  criado_por_nome: string | null
}

interface Resumo {
  saldo_inicial: number
  entradas: number
  saidas: number
  saldo_final: number
  periodo_inicio: string
  periodo_fim: string
  movimentacoes: Movimentacao[]
}

const TIPOS_MOVIMENTACAO = [
  { value: "ENTRADA", label: "Entrada", icon: ArrowDownCircle, color: "text-green-600" },
  { value: "SAIDA", label: "Saida", icon: ArrowUpCircle, color: "text-red-600" },
  { value: "AJUSTE", label: "Ajuste", icon: Banknote, color: "text-amber-600" },
]

const CATEGORIAS_ENTRADA = ["Venda", "Outros"]
const CATEGORIAS_SAIDA = ["Despesa", "Salario", "Equipamento", "Racao", "Medicamento", "Outros"]

function CaixaPage() {
  const apiBase = API_BASE
  const { user, apiFetch, isSuperAdmin, selectedTenantId } = useAuth()
  const [resumo, setResumo] = useState<Resumo | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [periodoInicio, setPeriodoInicio] = useState(() => {
    const d = new Date()
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split("T")[0]
  })
  const [periodoFim, setPeriodoFim] = useState(todayISODate)
  const [form, setForm] = useState({
    tipo: "ENTRADA",
    categoria: "",
    valor: 0,
    data_movimentacao: todayISODate(),
    descricao: "",
  })

  useEffect(() => {
    if (!user) return
    if (isSuperAdmin && !selectedTenantId) return
    fetchResumo()
  }, [user, isSuperAdmin, selectedTenantId, periodoInicio, periodoFim])

  const fetchResumo = async () => {
    try {
      const params = new URLSearchParams()
      if (periodoInicio) params.append("periodo_inicio", periodoInicio)
      if (periodoFim) params.append("periodo_fim", periodoFim)
      const res = await apiFetch(`${apiBase}/api/caixa/resumo?${params.toString()}`)
      if (res.ok) {
        setResumo(await res.json())
      }
    } catch (e) {
      console.error(e)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.valor || form.valor <= 0) {
      setSubmitError("Valor deve ser maior que zero.")
      return
    }
    setSubmitError(null)
    const payload = {
      tipo: form.tipo,
      categoria: form.categoria.trim() || null,
      valor: Number(form.valor),
      data_movimentacao: form.data_movimentacao,
      descricao: form.descricao.trim() || null,
    }
    try {
      const res = await apiFetch(`${apiBase}/api/caixa/movimentacoes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (res.ok) {
        setShowForm(false)
        setForm({
          tipo: "ENTRADA",
          categoria: "",
          valor: 0,
          data_movimentacao: todayISODate(),
          descricao: "",
        })
        fetchResumo()
      } else {
        const errData = await res.json().catch(() => ({})) as { detail?: string }
        setSubmitError(typeof errData?.detail === "string" ? errData.detail : "Erro ao salvar.")
      }
    } catch (e) {
      console.error(e)
      setSubmitError("Erro de conexao.")
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
  }

  const getTipoIcon = (tipo: string) => {
    const found = TIPOS_MOVIMENTACAO.find(t => t.value === tipo)
    if (!found) return null
    const Icon = found.icon
    return <Icon className={`h-4 w-4 ${found.color}`} />
  }

  const categoriasPorTipo = form.tipo === "ENTRADA" 
    ? CATEGORIAS_ENTRADA 
    : form.tipo === "SAIDA" 
      ? CATEGORIAS_SAIDA 
      : []

  if (isSuperAdmin && !selectedTenantId) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Selecione uma granja no menu lateral.</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Caixa</h1>
        <Button onClick={() => {
          setSubmitError(null)
          setShowForm(true)
        }}>
          <Plus className="h-4 w-4 mr-2" /> Nova Movimentacao
        </Button>
      </div>

      <div className="flex gap-2 items-end">
        <div className="space-y-1">
          <Label className="text-xs">Periodo Inicio</Label>
          <Input
            type="date"
            value={periodoInicio}
            onChange={(e) => setPeriodoInicio(e.target.value)}
            className="w-40"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Periodo Fim</Label>
          <Input
            type="date"
            value={periodoFim}
            onChange={(e) => setPeriodoFim(e.target.value)}
            className="w-40"
          />
        </div>
      </div>

      {resumo && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Saldo Inicial</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{formatCurrency(resumo.saldo_inicial)}</p>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50 dark:bg-green-950/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-green-700 dark:text-green-300">Entradas</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-700 dark:text-green-300">{formatCurrency(resumo.entradas)}</p>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-red-700 dark:text-red-300">Saidas</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-red-700 dark:text-red-300">{formatCurrency(resumo.saidas)}</p>
            </CardContent>
          </Card>
          <Card className={resumo.saldo_final >= 0 ? "border-blue-200 bg-blue-50 dark:bg-blue-950/20" : "border-red-200 bg-red-50 dark:bg-red-950/20"}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm font-medium ${resumo.saldo_final >= 0 ? "text-blue-700 dark:text-blue-300" : "text-red-700 dark:text-red-300"}`}>
                Saldo Final
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`text-2xl font-bold ${resumo.saldo_final >= 0 ? "text-blue-700 dark:text-blue-300" : "text-red-700 dark:text-red-300"}`}>
                {formatCurrency(resumo.saldo_final)}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog
        open={showForm}
        onOpenChange={(open) => {
          if (!open) {
            setShowForm(false)
            setSubmitError(null)
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto gap-0 p-0">
          <DialogTitle className="sr-only">Nova Movimentacao</DialogTitle>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold pr-10">Nova Movimentacao</h2>
            {submitError && (
              <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                {submitError}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Tipo *</Label>
                <div className="flex gap-2">
                  {TIPOS_MOVIMENTACAO.map((t) => {
                    const Icon = t.icon
                    return (
                      <Button
                        key={t.value}
                        type="button"
                        variant={form.tipo === t.value ? "default" : "outline"}
                        onClick={() => setForm({ ...form, tipo: t.value, categoria: "" })}
                        className="flex-1"
                      >
                        <Icon className={`h-4 w-4 mr-2 ${form.tipo === t.value ? "" : t.color}`} />
                        {t.label}
                      </Button>
                    )
                  })}
                </div>
              </div>
              
              {categoriasPorTipo.length > 0 && (
                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                    value={form.categoria}
                    onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                  >
                    <option value="">Selecione...</option>
                    {categoriasPorTipo.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Valor *</Label>
                  <Input
                    type="number"
                    min={0.01}
                    step="0.01"
                    value={form.valor || ""}
                    onChange={(e) => setForm({ ...form, valor: parseFloat(e.target.value) || 0 })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data *</Label>
                  <Input
                    type="date"
                    value={form.data_movimentacao}
                    onChange={(e) => setForm({ ...form, data_movimentacao: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descricao</Label>
                <textarea
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.descricao}
                  onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit">Salvar</Button>
                <Button variant="outline" type="button" onClick={() => { setShowForm(false); setSubmitError(null) }}>
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {resumo && resumo.movimentacoes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Movimentacoes do Periodo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Data</th>
                    <th className="text-left py-2">Tipo</th>
                    <th className="text-left py-2">Categoria</th>
                    <th className="text-left py-2">Descricao</th>
                    <th className="text-right py-2">Valor</th>
                    <th className="text-left py-2">Usuario</th>
                  </tr>
                </thead>
                <tbody>
                  {resumo.movimentacoes.map((m) => (
                    <tr key={m.id} className="border-b">
                      <td className="py-2">{formatBRDate(m.data_movimentacao)}</td>
                      <td className="py-2">
                        <div className="flex items-center gap-1">
                          {getTipoIcon(m.tipo)}
                          <span className={
                            m.tipo === "ENTRADA" ? "text-green-600" :
                            m.tipo === "SAIDA" ? "text-red-600" :
                            "text-amber-600"
                          }>
                            {m.tipo}
                          </span>
                        </div>
                      </td>
                      <td className="py-2">{m.categoria || "-"}</td>
                      <td className="py-2">{m.descricao || "-"}</td>
                      <td className={`text-right py-2 font-medium ${
                        m.tipo === "ENTRADA" ? "text-green-600" :
                        m.tipo === "SAIDA" ? "text-red-600" :
                        "text-amber-600"
                      }`}>
                        {m.tipo === "SAIDA" ? "-" : "+"}{formatCurrency(m.valor)}
                      </td>
                      <td className="py-2 text-muted-foreground">{m.criado_por_nome || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {resumo && resumo.movimentacoes.length === 0 && (
        <p className="text-muted-foreground text-center py-8">
          Nenhuma movimentacao no periodo.
        </p>
      )}
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/caixa')({
  component: CaixaPage,
})
